// src/ShoppingCartContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "../../core/api/axios";
import { useAuth } from "../../core/auth/AuthContext";
import usePopup from "../../core/hooks/usePopup";
import { executeAction } from "../../core/forms/CustomButtons"; // ✅ nodig voor headerinfo functions

const ShoppingCartContext = createContext(null);

const storageKey = (companyId, userId) => `ia_cart_${companyId || "x"}_${userId || "x"}`;

export function ShoppingCartProvider({ children }) {
  const { user, companyId } = useAuth();
  const userId = user?.id || user?.user_id || null;
  const userEmail = user?.email || "";
  const userName = `${user?.firstname || ""} ${user?.lastname || ""}`.trim() || user?.email || "User";

  const popup = usePopup(3000);

  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [cartConfig, setCartConfig] = useState(null); // headerButton config (Shopping Cart)

  // ✅ NEW: order header info state
  const [orderHeader, setOrderHeader] = useState({
    loading: false,
    values: {},   // { FK_RELATION: 123, FK_SENDERADDRESS: 456, REFERENCE: "...", ... }
    options: {},  // { FK_RELATION: [{value,label}], FK_SENDERADDRESS: [...] }
  });

  const showApiError = (err, fallbackMsg = "Unexpected error") => {
    const msg =
      err?.response?.data?.error ||
      err?.response?.data?.msg ||
      err?.message ||
      fallbackMsg;
    popup?.flashError?.(msg);
  };

  // Load from localStorage, then sync with server
  useEffect(() => {
    if (!companyId || !userId) {
      setItems([]);
      setIsOpen(false);
      setCartConfig(null);
      setOrderHeader({ loading: false, values: {}, options: {} });
      return;
    }

    const key = storageKey(companyId, userId);
    const cached = localStorage.getItem(key);
    if (cached) {
      try { setItems(JSON.parse(cached)); } catch {}
    }

    (async () => {
      try {
        const res = await api.post("/api/shopping_cart/get", { companyId });
        setItems(res.data?.data || []);
      } catch (err) {
        if (err?.response?.status !== 401) showApiError(err, "Unable to load shopping cart.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, userId]);

  // persist cache
  useEffect(() => {
    if (!companyId || !userId) return;
    localStorage.setItem(storageKey(companyId, userId), JSON.stringify(items));
  }, [items, companyId, userId]);

  const refresh = async () => {
    if (!companyId) return;
    const res = await api.post("/api/shopping_cart/get", { companyId });
    setItems(res.data?.data || []);
  };

  // ✅ openCart always sets config (even null) to avoid stale config
  const openCart = (config = null) => {
    setCartConfig(config || null);
    setIsOpen(true);

    // reset header state when opening (fresh load per open)
    setOrderHeader({ loading: false, values: {}, options: {} });
  };

  const closeCart = () => setIsOpen(false);

  // badge stats
  const lineCount = useMemo(() => (items || []).length, [items]);
  const totalQuantity = useMemo(() => {
    let t = 0;
    for (const it of (items || [])) t += Number(it.quantity || 0);
    return t;
  }, [items]);

  const addItem = async ({ moduleId, recordId, fieldsToShow, data }) => {
    if (!companyId || !userId) return popup.flashError("Not logged in.");
    if (moduleId == null || recordId == null) return popup.flashError("moduleId and recordId are required.");

    // optimistic update
    setItems(prev => {
      const idx = prev.findIndex(x =>
        String(x.module_id) === String(moduleId) &&
        String(x.record_id) === String(recordId)
      );
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: (copy[idx].quantity || 1) + 1 };
        return copy;
      }
      return [
        ...prev,
        {
          id: `tmp-${Date.now()}`,
          company_id: companyId,
          user_id: userId,
          module_id: moduleId,
          record_id: String(recordId),
          quantity: 1,
          fields_json: { fieldsToShow: fieldsToShow || [], data: data || {} },
        }
      ];
    });

    try {
      await api.post("/api/shopping_cart/add", {
        companyId,
        moduleId,
        recordId: String(recordId),
        quantity: 1,
        fields: { fieldsToShow: fieldsToShow || [], data: data || {} }
      });
      await refresh();
      popup.flashSuccess?.("Added to shopping cart.");
    } catch (err) {
      try { await refresh(); } catch {}
      if (err?.response?.status !== 401) showApiError(err, "Failed to add item to cart.");
    }
  };

  const removeItem = async (item) => {
    if (!companyId || !userId) return popup.flashError("Not logged in.");
    const moduleId = item?.module_id;
    const recordId = item?.record_id;
    if (moduleId == null || recordId == null) return popup.flashError("moduleId and recordId are required.");

    // optimistic
    setItems(prev =>
      prev.filter(x => !(String(x.module_id) === String(moduleId) && String(x.record_id) === String(recordId)))
    );

    try {
      await api.post("/api/shopping_cart/remove", { companyId, moduleId, recordId: String(recordId) });
      popup.flashSuccess?.("Removed from shopping cart.");
      await refresh();
    } catch (err) {
      try { await refresh(); } catch {}
      if (err?.response?.status !== 401) showApiError(err, "Failed to remove item.");
    }
  };

  const setQuantity = async (item, qty) => {
    if (!companyId || !userId) return popup.flashError("Not logged in.");
    const moduleId = item?.module_id;
    const recordId = item?.record_id;
    if (moduleId == null || recordId == null) return popup.flashError("moduleId and recordId are required.");

    const parsed = parseInt(qty, 10);
    if (!Number.isFinite(parsed)) return popup.flashError("Quantity must be a number.");
    const q = Math.max(1, parsed);

    // optimistic
    setItems(prev => prev.map(x =>
      (String(x.module_id) === String(moduleId) && String(x.record_id) === String(recordId))
        ? { ...x, quantity: q }
        : x
    ));

    try {
      await api.post("/api/shopping_cart/update_qty", {
        companyId, moduleId, recordId: String(recordId), quantity: q
      });
      await refresh();
    } catch (err) {
      try { await refresh(); } catch {}
      if (err?.response?.status !== 401) showApiError(err, "Failed to update quantity.");
    }
  };

  // fallback purchase (laten we voorlopig ongemoeid)
  const purchase = async () => {
    if (!companyId || !userId) {
      popup.flashError("Not logged in.");
      return null;
    }
    try {
      const res = await api.post("/api/shopping_cart/purchase", { companyId });
      if (res.data?.success) {
        setItems([]);
        closeCart();
        popup.flashSuccess?.(res.data?.message || "Purchase created.");
      } else {
        popup.flashError(res.data?.error || res.data?.message || "Purchase failed.");
      }
      return res.data;
    } catch (err) {
      if (err?.response?.status !== 401) showApiError(err, "Purchase failed.");
      return null;
    }
  };

  // ✅ NEW: set a single header field value (and clear dependents client-side if you want)
  const setOrderFieldValue = (field, value) => {
    setOrderHeader((prev) => ({
      ...prev,
      values: { ...prev.values, [field]: value },
    }));
  };

  /**
   * ✅ NEW: load header info using ERP function specified in cartConfig
   * calls executeAction(fnName, ctx) which will call your ERP function in CustomButtons.js
   */
  const loadOrderHeaderInfo = async () => {
    const fnName = cartConfig?.shopping_cart_headerInfo_function;
    const connectionId = cartConfig?.shopping_cart_headerInfo_connection_id;

    if (!fnName || !connectionId) return;

    setOrderHeader((p) => ({ ...p, loading: true }));

    try {
      await executeAction(fnName, {
        companyId,
        connectionId,
        userEmail,
        userName,
        popup,

        cartConfig,
        orderHeader,
        setOrderHeader,
      });
    } catch (err) {
      showApiError(err, "Failed to load order header info.");
    } finally {
      setOrderHeader((p) => ({ ...p, loading: false }));
    }
  };

  const value = useMemo(() => ({
    isOpen,
    openCart,
    closeCart,
    items,
    addItem,
    removeItem,
    setQuantity,
    purchase,
    refresh,

    cartConfig,
    lineCount,
    totalQuantity,

    // ✅ NEW exports
    orderHeader,
    setOrderFieldValue,
    loadOrderHeaderInfo,

    // (optioneel) popup meegeven
    popup,
  }), [
    isOpen, items, cartConfig, lineCount, totalQuantity,
    orderHeader
  ]);

  return (
    <ShoppingCartContext.Provider value={value}>
      {children}
      {popup.view}
    </ShoppingCartContext.Provider>
  );
}

export function useShoppingCart() {
  const ctx = useContext(ShoppingCartContext);
  if (!ctx) throw new Error("useShoppingCart must be used inside ShoppingCartProvider");
  return ctx;
}
